import { z } from 'zod'

/**
 * `localStorage['clearroute:recentSearches']` — recent search selections.
 *
 * Contract (Plan 02-03 Task 4, D-81, T-2-LS):
 *   - Cap at 10 items, newest first.
 *   - Dedup by `id` on write (previous entry with same id removed before
 *     prepending so the most-recent savedAt wins).
 *   - 7-day TTL — items older than TTL_MS are filtered on read AND scrubbed
 *     back to storage so stale poisoned entries cannot live forever.
 *   - Zod validation on read (Version-gated envelope + per-item schema).
 *   - Fail-open: corrupted JSON, schema mismatch, QuotaExceededError, or
 *     localStorage access errors all return [] / swallow silently so the
 *     search UI never crashes on a bad persisted state.
 */

export const STORAGE_KEY = 'clearroute:recentSearches'
export const TTL_MS = 7 * 24 * 60 * 60 * 1000 // 7 days — D-81
export const MAX_RECENTS = 10

const RecentSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1).max(200),
  sublabel: z.string().max(200).optional(),
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
  savedAt: z.number().int().positive(),
})

const RecentsEnvelopeSchema = z.object({
  version: z.literal(1),
  items: z.array(RecentSchema).max(MAX_RECENTS),
})

export type Recent = z.infer<typeof RecentSchema>

function safeGet(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
}

function safeSet(value: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, value)
  } catch {
    // QuotaExceededError or Safari private-mode — swallow. Search UI keeps
    // working; next session just doesn't have this entry in recents.
  }
}

function safeRemove(): void {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // ignore — symmetric with safeSet.
  }
}

function writeEnvelope(items: Recent[]): void {
  const envelope = { version: 1 as const, items: items.slice(0, MAX_RECENTS) }
  safeSet(JSON.stringify(envelope))
}

/**
 * Load, validate, TTL-filter, and opportunistically scrub the recents list.
 *
 * Returns [] when:
 *   - key absent
 *   - JSON.parse throws
 *   - Zod envelope validation fails (e.g. version mismatch, bad item shape)
 *   - localStorage access throws
 */
export function readRecents(): Recent[] {
  const raw = safeGet()
  if (raw == null) return []

  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return []
  }

  const result = RecentsEnvelopeSchema.safeParse(parsed)
  if (!result.success) return []

  const now = Date.now()
  const fresh = result.data.items.filter((it) => now - it.savedAt < TTL_MS)

  // Side effect: if we dropped anything, scrub storage so stale entries
  // don't persist forever (T-2-LS mitigation).
  if (fresh.length !== result.data.items.length) {
    writeEnvelope(fresh)
  }
  return fresh
}

/**
 * Prepend a selection, dedup by id, cap at 10. Most-recent savedAt wins.
 */
export function writeRecent(r: Recent): void {
  const current = readRecents()
  const deduped = current.filter((it) => it.id !== r.id)
  const next = [r, ...deduped].slice(0, MAX_RECENTS)
  writeEnvelope(next)
}

export function clearRecents(): void {
  safeRemove()
}
